import { concat } from '@ember/helper';
import { eq, or } from 'tio-ui/utilities';
import {
  getSingleValueForTasField,
  getSortedOptionsByFieldName,
} from '../../../../utils/tuition-assistance/fields.ts';
import { Section, VStack } from 'tio-reimagined/components/layout';
import { t } from 'ember-intl';
import ApprovalAttachmentsReview from './approval-attachments-review.gts';
import Component from '@glimmer/component';
import inc from '../../../../helpers/inc.ts';
import Table from 'tio-reimagined/components/table';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/models/tas-program-template';
import type { TemplateApprovalWorkflowApprover } from '../../../../types/tuition-assistance.ts';
import type { TemplateFieldName } from '../../../../utils/tuition-assistance/fields.ts';
import type TasFieldOptionModel from 'tio-data/models/tas-field-option';

interface TuitionAssistanceProgramTemplateReviewApprovalWorkflowApprovalLayoutReviewSignature {
  Args: {
    approvalWorkflowName:
      | 'PROGRAM_APPROVE_WORKFLOW'
      | 'PROGRAM_APPROVE_WITHDRAW_COMPLETE_WORKFLOW'
      | 'APPLICATION_APPROVE_COURSES_WORKFLOW'
      | 'APPLICATION_APPROVE_EVIDENCE_WORKFLOW';
    approverList: TemplateApprovalWorkflowApprover[];
    attachmentData: {
      required: boolean;
      visible: boolean;
      text: string;
      values: string[];
    };
    hideAttachmentFields?: boolean;
    reviewFields: TASProgramTemplateModelFieldsSignature;
    tasFieldOptions?: TasFieldOptionModel[];
    workflowStepName: string;
    workflowStepType: string;
    cannedApproverResponses: {
      [key: string]: TemplateFieldName;
    };
  };
}

export default class TuitionAssistanceProgramTemplateReviewApprovalWorkflowApprovalLayoutReviewComponent extends Component<TuitionAssistanceProgramTemplateReviewApprovalWorkflowApprovalLayoutReviewSignature> {
  getSingleValueForTasField = getSingleValueForTasField;
  getSortedOptionsByFieldName = getSortedOptionsByFieldName;
  isProgramRequired(
    field: TemplateFieldName,
    programTemplateFields: TASProgramTemplateModelFieldsSignature
  ) {
    if (!field || !programTemplateFields) {
      return '';
    }
    return programTemplateFields?.[field]?.required ? 'Yes' : 'No';
  }

  <template>
    {{! Approval Workflow Section }}
    <Section>
      <:header>
        {{t "common.tuition_assistance.program_template_review.approval_workflow.default"}}
      </:header>
      <:body>
        <VStack @collapsed={{true}}>
          <Table>
            <:thead as |options|>
              <options.tr>
                <options.th>
                  {{t
                    "common.tuition_assistance.program_template_review.approval_workflow.workflow_step_name"
                  }}
                </options.th>
                <options.th>{{t "common.required"}}</options.th>
                <options.th>
                  {{t "common.tuition_assistance.program_template_review.workflow_step_type"}}
                </options.th>
                <options.th>
                  {{t "common.tuition_assistance.program_template_review.approver"}}
                </options.th>
                <options.th>
                  {{t "common.tuition_assistance.program_template_review.approver_role"}}
                </options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              <options.tr>
                <options.td>{{@workflowStepName}}</options.td>
                <options.td>
                  {{this.isProgramRequired @approvalWorkflowName @reviewFields}}
                </options.td>
                <options.td>{{@workflowStepType}}</options.td>
                <options.td>
                  <div class="grid grid-flow-row auto-rows-max grid-cols-3 gap-4">
                    {{#each @approverList as |approver index|}}
                      <div class="col-span-1 font-semibold ml-5">
                        {{inc index}}
                      </div>
                      <div class="col-span-2 flex">
                        <div class="w-80">
                          {{t
                            (concat "common.program_builder.approval_workflow.roles." approver.role)
                          }}
                          {{#if (eq approver.role "CUSTOM_APPROVER")}}
                            <p class="ml-4">
                              {{t "common.tuition_assistance.program_template_review.employee_id"}}:
                              <span class="font-semibold">{{approver.employeeId}}</span></p>
                          {{/if}}
                        </div>
                      </div>
                    {{/each}}
                  </div>
                </options.td>
              </options.tr>
            </:tbody>
          </Table>
          <Section>
            <div class="col-span-6">
              {{#each-in @cannedApproverResponses as |key fieldName|}}
                <div class="tio-col-extra-tight py-2">
                  <div class="font-medium">
                    {{t (concat "common.tuition_assistance.program_template_review." key)}}:
                  </div>
                  {{#let (this.getSortedOptionsByFieldName fieldName @tasFieldOptions) as |opts|}}
                    {{#if opts.length}}
                      <ul class="list-disc list-inside pl-4 space-y-1">
                        {{#each opts as |option|}}
                          <li>
                            <span class="font-semibold">{{option.label}}</span>:
                            {{option.value}}
                          </li>
                        {{/each}}
                      </ul>
                    {{else}}
                      <div>
                        {{! @glint-expect-error: Only primitive values and certain DOM objects (see 'ContentValue' in '@glint/template') are usable as top-level template content.}}
                        {{or
                          (getSingleValueForTasField fieldName @reviewFields)
                          (t "common.tuition_assistance.program_template_review.none_provided")
                        }}
                      </div>
                    {{/if}}
                  {{/let}}
                </div>
              {{/each-in}}
            </div>
          </Section>
          {{#unless @hideAttachmentFields}}
            <Section>
              <ApprovalAttachmentsReview @attachmentData={{@attachmentData}} />
            </Section>
          {{/unless}}
        </VStack>
      </:body>
    </Section>
  </template>
}
